import $ from 'jquery';
import anime from 'animejs';

const namespace = 'accordion';

export default class Accordion {
  constructor(el, options) {
    this.options = $.extend(
      true,
      {
        eventNamespace: 'uiAccordion',
        fireEvents: false,
        touchScrollDistance: 20,
        selectors: {
          item: '.accordion-item',
          anchor: '.accordion-link',
          list: '.accordion-row',
        },
        classes: {
          init: 'is-enabled',
          active: 'is-active',
          tapped: 'is-tapped',
        },
        animate: true,
      },
      options,
    );

    this.$el = $(el);
    this.$el.data(namespace, this);
    this.$el.addClass(this.options.classes.init);

    this.bind();
    this.fireEvent('afterInit');
    if (el === '.megamenu__container') {
      this.subAccordions();
    }
  }

  subAccordions() {
    const arr = Array.from(document.querySelectorAll('[data-megamenu-subcontainer]'));
    arr.forEach(item => {
      new Accordion(item, {
        selectors: {
          item: '[data-megamenu-subitem]',
          anchor: '[data-megamenu-sublink]',
          list: '[data-megamenu-subdropdown]',
        },
      });
    });
  }

  /**
   * Apply event listeners
   */
  bind() {
    this.$el.on(
      `touchstart.${namespace}`,
      this.options.selectors.anchor,
      this.handleTouchStart.bind(this),
    );

    this.$el.on(
      `touchend.${namespace} touchcancel.${namespace}`,
      this.options.selectors.anchor,
      this.handleTouchEnd.bind(this),
    );

    this.$el.on(
      `click.${namespace}`,
      this.options.selectors.anchor,
      this.handleClick.bind(this),
    );
  }

  /**
   * Touch start event handler
   */
  handleTouchStart() {
    this.touchstartPos = $(window).scrollTop();
  }

  /**
   * Touch end event handler
   * @param {Event} event
   */
  handleTouchEnd(event) {
    const distance = this.touchstartPos - $(window).scrollTop();
    const $parent = $(event.currentTarget).parent(this.options.selectors.item);
    const $child = $parent.find(this.options.selectors.list).eq(0);

    if ($child.length) {
      event.preventDefault();
    }

    if (Math.abs(distance) < this.options.touchScrollDistance) {
      this.fireEvent('beforeTap', this.$el);

      this.$el.addClass(this.options.classes.tapped);
      this.toggle($parent);

      this.fireEvent('afterTap', this.$el);
    }
  }

  /**
   * Link click handler
   * @param {Event} event
   */
  handleClick(event) {
    const $target = $(event.currentTarget);
    const $parent = $target.parent(this.options.selectors.item);
    const $child = $parent.find(this.options.selectors.list).eq(0);

    if ($child.length) {
      event.preventDefault();
    }

    this.toggle($parent);
    this.fireEvent('afterClick', $target);
  }

  toggle($node) {
    const isActive = $node.hasClass(this.options.classes.active);

    if (isActive) {
      this.hide($node);
    } else {
      this.open($node);
    }
  }

  /**
   * Show the given nodes
   * @param {jQuery} $node
   */
  open($node) {
    const $title = $node.find(this.options.selectors.anchor);
    const $list = $node.find(this.options.selectors.list);
    const heights = $list.children().map((index, el) => {
      return $(el).outerHeight();
    }).toArray();
    const finalHeight = heights.reduce((curr, accu) => curr + accu, 0);

    this.fireEvent('beforeOpen', $node);

    $title
      .attr('aria-expanded', true);

    $node
      .addClass(this.options.classes.active)
      .attr('aria-hidden', false);

    anime({
      targets: $list.get(),
      height: finalHeight,
      easing: 'cubicBezier(.420, 0, 1, 1)',
      delay: 0,
      duration: 300,
    });

    $list[0].setAttribute('data-full-height', finalHeight);
    setTimeout(() => $list[0].style.height = 'auto', 400);

    this.fireEvent('afterOpen', $node);
  }

  /**
   * Hide the given nodes
   * @param {jQuery} $nodes
   */
  hide($node) {
    const $list = $node.find(this.options.selectors.list);

    $node
      .attr('aria-hidden', true)
      .removeClass(this.options.classes.active);

    $list[0].style.height = `${$list[0].dataset.fullHeight}px`;

    anime({
      targets: $list.get(),
      height: 0,
      easing: 'cubicBezier(.000, 0, .580, 1)',
      duration: 300,
    });
  }

  /**
   * @param {String} eventName
   * @param {jQuery} $node
   */
  fireEvent(eventName, $node = this.$el) {
    if (this.options.fireEvents) {
      $node.trigger(`${this.options.eventNamespace}:${eventName}`);
    }
  }

  /**
   * Remove all traces of the instance
   */
  destroy() {
    const $nodes = this.$el.find(`.${this.options.classes.active}`);
    const $lists = this.$el.find(this.options.selectors.list);

    $nodes.push(this.$el);

    this.$el.removeClass(this.options.classes.init);

    if ($nodes.length) {
      $nodes.removeClass(this.options.classes.active);
    }

    $lists.css('height', '');

    this.$el.off(`touchstart.${namespace}`);
    this.$el.off(`touchend.${namespace} touchcancel.${namespace}`);
    this.$el.off(`click.${namespace}`);
    this.$el.removeData(namespace);
  }
}

class AccordionElement extends HTMLElement {
  constructor() {
    super();
    this.accordion = this.querySelector(".accordion");
    this.panel = this.querySelector(".panel");
    this.scrollIntoContent = this.scrollIntoContent.bind(this);
    this.isReviews = this.isReviews.bind(this);
    this.accordion.addEventListener("click", this.toggleAccordion.bind(this));

    if (this.panel) {
      this.panel.style.transition = "max-height 0.3s ease-in-out";
    }
  }

  isMobile() {
    return window.innerWidth < 1024;
  }

  isReviews() {
    return this.dataset.reviews === "true";
  }

  toggleAccordion() {
    const productAccordions = document.querySelector("product-accordions");
    if (productAccordions) {
      productAccordions.closeAccordions();
    }
    if (this.accordion) {
      if (this.accordion.classList.contains("active")) {
        this.panel.style.maxHeight = "0";
        this.panel.classList.toggle("open");
        if (this.classList.contains("child")) {
          const parentPanel = this.closest(".panel");
          const parentPanelHeight = parentPanel.scrollHeight;
          parentPanel.style.maxHeight = parentPanelHeight + "px";
        }
        this.accordion.classList.remove("active");
        if (!this.panel.dataset.panel) return;
        document.documentElement.classList.toggle(this.panel.dataset.panel);
      } else {
        this.panel.classList.toggle("open");     
        if (this.classList.contains("child")) {
          const parentPanel = this.closest(".panel");
          const parentPanelHeight = parentPanel.scrollHeight;
          const childPanelHeight = this.panel.scrollHeight;
          const combinedHeight = parentPanelHeight + childPanelHeight;
          parentPanel.style.maxHeight = combinedHeight + "px";
          this.panel.style.maxHeight = combinedHeight + "px";
        } else {
          this.panel.style.maxHeight = this.panel.scrollHeight + "px";
        }
        this.accordion.classList.add("active");

        if (!this.panel.dataset.panel) return;
        document.documentElement.classList.toggle(this.panel.dataset.panel);
      }
    }
  }

  scrollIntoContent() {
      const rect = this.accordion.getBoundingClientRect();
      const top = rect.top + window.scrollY;
      let offset;
      
      if (this.isMobile()) {
        offset = -64;
      } else {
        offset = -100;
      }
      
      window.scrollTo({
        top: top + offset,
        behavior: 'smooth',
      });
    }
}

customElements.define("accordion-element", AccordionElement);